/**
 * Generic, subject-scoped attachment hooks.
 *
 * The attachments backend is polymorphic: every file is a `UserUploadedFile`
 * tagged with `subject_type` + `subject_uuid` (+ a free-text `category` that
 * acts as its label), and the REST surface is generic
 * (`/files/attachments/<subject_type>/<subject_uuid>/`, `/files/upload/`,
 * `/files/attachments/<uuid>/delete/`). These hooks are the claim
 * `use-claim-attachments` hooks generalised to any registered subject
 * (`claim`, `transfer`, ...), so a surface like the transfer Documents card
 * reuses the exact same list / upload / delete behaviour.
 *
 * The `Attachment` type and the defensive list-payload normaliser are shared
 * with the claim hooks - imported here rather than duplicated.
 */

import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import i18n from 'i18next'

import api from '@/admin/lib/api'
import { invalidateSoon } from '@/shared/lib/invalidate-soon'
import { toastApiError } from '@/shared/lib/api-error'
import {
  ZIP_PART_BYTES,
  discardZipTarget,
  downloadFilesAsZip,
  pickZipTarget,
  planZipParts,
  splitManifestCopies,
  type ZipDownloadProgress,
  type ZipManifestEntry,
  type ZipTarget,
} from '@/shared/lib/zip-download'
import {
  normaliseAttachmentListPayload,
  type Attachment,
} from '@/admin/pages/claims/use-claim-attachments'

export type { Attachment } from '@/admin/pages/claims/use-claim-attachments'

/** React Query key for a subject's attachment list. */
export function subjectAttachmentsKey(
  subjectType: string,
  subjectUuid: string | undefined,
  includeDeleted = false,
): [string, string, string | undefined, boolean] {
  return ['subject-attachments', subjectType, subjectUuid, includeDeleted]
}

export function useSubjectAttachments(
  subjectType: string,
  subjectUuid: string | undefined,
  options?: { includeDeleted?: boolean },
) {
  const includeDeleted = !!options?.includeDeleted
  return useQuery<unknown, Error, Attachment[]>({
    queryKey: subjectAttachmentsKey(subjectType, subjectUuid, includeDeleted),
    enabled: !!subjectUuid,
    queryFn: async () => {
      const params = includeDeleted ? { include_deleted: 1 } : {}
      const res = await api.get(
        `/files/attachments/${subjectType}/${subjectUuid}/`,
        { params },
      )
      return res.data
    },
    // Runs on fresh network data AND values rehydrated from the persisted
    // cache, so both paths are guaranteed to yield an array.
    select: normaliseAttachmentListPayload,
  })
}

/**
 * Upload a document and attach it to a subject under a free-text `category`
 * (the label). Lands in the subject's attachment registry so it appears in
 * the card immediately.
 */
export function useUploadSubjectEvidence(
  subjectType: string,
  subjectUuid: string | undefined,
  useCase: string,
) {
  const qc = useQueryClient()
  return useMutation<
    Attachment,
    unknown,
    { file: File; category: string; clientUploadId?: string }
  >({
    mutationFn: async ({ file, category, clientUploadId }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('use_case', useCase)
      form.append('subject_type', subjectType)
      if (subjectUuid) form.append('subject_uuid', subjectUuid)
      if (category) form.append('category', category)
      // Same value on every retry of one file, so the server stores it once.
      if (clientUploadId) form.append('client_upload_id', clientUploadId)
      const res = await api.post('/files/upload/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => {
      // Once per few seconds during a bulk upload, not once per file.
      invalidateSoon(qc, ['subject-attachments', subjectType, subjectUuid])
    },
  })
}

/**
 * Relabel an already-uploaded attachment.
 *
 * The label is the file's free-text `category`, which was write-once at
 * upload time on every subject except claims - a mis-labelled document was
 * only fixable by deleting and re-uploading it, losing the original
 * attribution and date (WPS-179). Same endpoint the claim Evidence card
 * uses; the backend gates it on the subject adapter's `can_delete`, on the
 * grounds that re-filing a document is the same order of authority as
 * removing it.
 */
export function useRecategoriseSubjectAttachment(
  subjectType: string,
  subjectUuid: string | undefined,
) {
  const qc = useQueryClient()
  return useMutation<Attachment, unknown, { uuid: string; category: string }>({
    mutationFn: async ({ uuid, category }) => {
      const res = await api.patch(`/files/attachments/${uuid}/category/`, {
        category,
        // Names the record for a file answering one of its form fields (the
        // Proof of Purchase captured while registering), which carries no
        // subject of its own. Ignored for every other attachment.
        subject_type: subjectType,
        subject_uuid: subjectUuid,
      })
      return res.data
    },
    onSuccess: () => {
      // i18n via the instance, not the hook: this module is not a component.
      toast.success(i18n.t('attachments.labelUpdated', 'Label updated.'))
      qc.invalidateQueries({
        queryKey: ['subject-attachments', subjectType, subjectUuid],
      })
    },
    onError: (err) => {
      toastApiError(
        err,
        i18n.t('attachments.labelUpdateFailed', "Couldn't change the label."),
      )
    },
  })
}

export function useDeleteSubjectAttachment(
  subjectType: string,
  subjectUuid: string | undefined,
) {
  const qc = useQueryClient()
  return useMutation<Attachment, unknown, { uuid: string; reason?: string }>({
    mutationFn: async ({ uuid, reason }) => {
      const res = await api.post(`/files/attachments/${uuid}/delete/`, {
        reason: reason ?? '',
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Attachment deleted.')
      qc.invalidateQueries({
        queryKey: ['subject-attachments', subjectType, subjectUuid],
      })
    },
    onError: () => {
      toast.error("Couldn't delete the attachment.")
    },
  })
}

/**
 * "Download all" for a subject: every file it holds, as one zip.
 *
 * The manifest names the files; the browser fetches them a few at a time and
 * builds the zip itself (see ``downloadFilesAsZip``), so a claim with hundreds
 * of photos downloads in full. ``progress`` is null when idle.
 */
export function useDownloadAllAttachments(
  subjectType: string,
  subjectUuid: string,
  fileName: string,
  errorMessage: string,
) {
  const [progress, setProgress] = useState<ZipDownloadProgress | null>(null)

  const download = useCallback(async () => {
    let target: ZipTarget
    try {
      target = await pickZipTarget(fileName)
    } catch (err) {
      // Cancelling the save dialog is a choice, not a failure.
      if (err instanceof DOMException && err.name === 'AbortError') return
      toastApiError(err, errorMessage)
      return
    }
    setProgress({ done: 0, total: 0 })
    try {
      const res = await api.get(
        `/files/attachments/${subjectType}/${subjectUuid}/download-manifest/`,
      )
      const manifest = (Array.isArray(res.data) ? res.data : []) as ZipManifestEntry[]
      // Identical copies already in the package are left out and named in
      // NOT-INCLUDED.txt, so the zip holds each photo once (WPS-223).
      const { files: entries, copies } = splitManifestCopies(manifest)
      setProgress({ done: 0, total: entries.length })
      // Built in memory, a large package saves as several zips; say so first.
      const parts =
        target.kind === 'blob' ? planZipParts(entries, ZIP_PART_BYTES).length : 1
      if (parts > 1) {
        toast.info(i18n.t('attachments.downloadInParts', { count: parts }))
      }
      const { saved, skipped } = await downloadFilesAsZip(entries, target, setProgress, {
        copies,
      })
      if (skipped.length > 0) {
        toast.warning(
          i18n.t('attachments.downloadPartial', {
            saved,
            total: entries.length,
          }),
        )
      }
    } catch (err) {
      // The save dialog already created the file; do not leave it behind empty.
      await discardZipTarget(target)
      toastApiError(err, errorMessage)
    } finally {
      setProgress(null)
    }
  }, [subjectType, subjectUuid, fileName, errorMessage])

  return { download, progress, downloading: progress !== null }
}
